import {
  Avatar,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Tooltip,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import Dialog from "components/NoScrollDialog";
import DropdownSelect from "components/DropdownSelect";
import ExpandableButton from "components/ExpandableButton";
import LoadingButton from "components/LoadingButton";
import MDBadge from "components/MDBadge";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { usePermissionMonitor } from "components/PermissionMonitor";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { Link, useParams, useSearchParams } from "react-router-dom";
import auditsService from "services/audits-service";
import regionsService from "services/regions-service";
import usersService from "services/users-service";
import useObjectState from "utils/useObjectState";
import { EXTERNAL, INTERNAL, STATUS_COLOR } from "../../../../const";
import AuditDialogv2 from "./AuditDialog";

export default function AllAudits({ permissions }) {
  const { label } = useLanguage();
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({
    page: 1,
    sortBy: "",
  });
  const can = usePermissionMonitor();

  const [rows, setRows] = useState([]);
  const [audits, setAudits] = useState(null);
  const [count, setCount] = useState(null);
  const [bulkStatusMode, setBulkStatusMode] = useState(false);
  const [selectedAuditIds, setSelectedAuditIds] = useState([]);
  const [options, changeOptions] = useObjectState({
    statuses: null,
    users: null,
    regions: null,
  });
  const [open, changeOpen] = useObjectState({
    create: false,
    alert: false,
    bulkStatus: false,
  });
  const [csvData, setCsvData] = useState(null);
  const [lastRequestQuery, setLastRequestQuery] = useState(null);

  const selectableAuditIds = useMemo(
    () => audits?.map((audit) => audit.id) || [],
    [audits],
  );

  const columns = useMemo(() => {
    const tableColumns = [
      { Header: "", accessor: "type", align: "left" },
      {
        Header: label("branch-singular"),
        accessor: "branch",
        align: "left",
        width: "100%",
      },
      {
        Header: label("description-singular"),
        accessor: "description",
        align: "left",
        width: "100%",
      },
      {
        Header: label("date-singular"),
        accessor: "date",
        align: "left",
        width: "100%",
      },
      {
        Header: label("assigned-to-singular"),
        accessor: "assignedTo",
        align: "left",
        width: "100%",
      },
      {
        Header: label("status-singular"),
        accessor: "status",
        align: "left",
        width: "100%",
      },
      {
        Header: label("last-updated"),
        accessor: "lastUpdated",
        align: "left",
        width: "100%",
      },
      {
        Header: "",
        accessor: "actions",
        align: "left",
        width: "100%",
      },
    ];

    if (!bulkStatusMode) return tableColumns;

    return [
      {
        Header: (
          <Checkbox
            size="small"
            checked={
              selectableAuditIds.length > 0 &&
              selectedAuditIds.length === selectableAuditIds.length
            }
            indeterminate={
              selectedAuditIds.length > 0 &&
              selectedAuditIds.length < selectableAuditIds.length
            }
            onChange={(event) => {
              setSelectedAuditIds(
                event.target.checked ? selectableAuditIds : [],
              );
            }}
          />
        ),
        accessor: "select",
        align: "left",
        width: "48px",
      },
      ...tableColumns,
    ];
  }, [bulkStatusMode, label, selectableAuditIds, selectedAuditIds]);

  async function fetchAudits(cancelToken, sortBy, loadingId) {
    const requests = [
      usersService.getUsers({ view: "minimal" }, null, cancelToken),
      regionsService.getRegions(null, cancelToken),
    ];

    if (permissions.get) {
      requests.push(auditsService.getStatuses(null, cancelToken));
    }
    const values = await Promise.all(requests);

    let [response, data] = values[0];
    if (response.ok) {
      changeOptions("users", data);
    } else {
      console.log(`Error while fetching users: ${data.error}`);
    }

    [response, data] = values[1];
    if (response.ok) {
      changeOptions("regions", data);
    } else {
      console.log(`Error while fetching regions: ${data.error}`);
    }

    if (permissions.get) {
      [response, data] = values[2];
      if (response.ok) {
        changeOptions("statuses", data);
      } else {
        console.log(`Error while fetching statuses: ${data.error}`);
      }
    }
  }

  async function fetchAuditsOnly(request, newPage, sortBy) {
    const req = request ? { ...request } : { ...lastRequestQuery };
    req.skip = newPage
      ? (newPage - 1) * 10
      : (Number(searchParams.get("page") || 1) - 1) * 10;
    req.take = 10;
    req.sortBy = sortBy
      ? sortBy.replace("asce", "asc")
      : searchParams.get("sortBy").replace("asce", "asc");

    const [ok, data] = await auditsService.getAudits(req);

    if (ok) {
      setAudits(data.data);
      setCount(data.count);
    }
  }

  async function handleCreateAudit(newAudit) {
    newAudit = JSON.parse(JSON.stringify(newAudit));

    newAudit.date = new Date(newAudit.date).toISOString();

    const [response, data] = await auditsService.createAudit(newAudit);
    if (response.ok) {
      await fetchAuditsOnly();
      return { success: true };
    } else {
      console.log(`Error while creating audit: ${data.error}`);
      return { error: data.error };
    }
  }

  async function handleDeleteAudit(audit) {
    const [response, data] = await auditsService.removeAudit(audit.id);
    if (response.ok) {
      await fetchAuditsOnly();
      return { success: true };
    } else {
      console.log(`Error while deleting audit: ${data.error}`);
      return { error: `Error while deleting audit: ${data.error}` };
    }
  }

  async function handleBulkStatusUpdate({ statusId }) {
    const auditIds = [...new Set(selectedAuditIds)];
    const [response, data] = await auditsService.updateAuditsStatus({
      statusId,
      force: true,
      auditIds,
    });

    if (response.ok) {
      await fetchAuditsOnly();
      setSelectedAuditIds([]);
      setBulkStatusMode(false);
      return { success: true };
    }

    console.log(`Error while updating audits status: ${data.error}`);
    return { error: data.error || "Error while updating audits status" };
  }

  function toggleSelectedAudit(auditId) {
    setSelectedAuditIds((current) =>
      current.includes(auditId)
        ? current.filter((selectedAuditId) => selectedAuditId !== auditId)
        : [...current, auditId],
    );
  }

  const filterBySelectsItems = useMemo(() => {
    const filters = [
      {
        id: "status",
        items: [
          { value: 0, render: label("all") },
          ...(options?.statuses?.map((status) => {
            return {
              value: status.id,
              render: status.description,
            };
          }) || []),
        ],
        label: label("filter-by-status-label"),
      },
      {
        id: "regionId",
        items: [
          { value: 0, render: label("all") },
          ...(options.regions?.map((region) => {
            return {
              value: region.id,
              render: region.description,
            };
          }) || []),
        ],
        label: label("filter-by-region-label"),
      },
    ];

    if (!userId) {
      filters.push({
        id: "userId",
        items: [
          { value: 0, render: label("all") },
          ...(options.users?.map((user) => {
            return {
              value: user.id,
              render: user.name,
            };
          }) || []),
        ],
        label: label("filter-by-assignedto-label"),
      });
    }
    return filters;
  }, [userId, options]);

  const filterBys = useMemo(() => {
    const filters = [
      { value: "Description", label: label("description-singular") },
      { value: "Branch", label: label("branch-singular") },
    ];

    if (!userId) {
      filters.push({ value: "User", label: label("user-singular") });
    }
    return filters;
  }, [userId]);

  useEffect(() => {
    localStorage.setItem(
      "all-audits-page_filters",
      JSON.stringify({ sortBy: searchParams.get("sortBy") }),
    );
  }, [searchParams.get("sortBy")]);

  useEffect(() => {
    if (!audits) return;

    setRows(
      audits.map((audit) => {
        return {
          select: bulkStatusMode ? (
            <Checkbox
              size="small"
              checked={selectedAuditIds.includes(audit.id)}
              onChange={() => {
                toggleSelectedAudit(audit.id);
              }}
            />
          ) : null,
          type: (
            <Tooltip title={audit.type === EXTERNAL ? "External" : "Internal"}>
              <MDBadge
                badgeContent={audit.type === EXTERNAL ? "EXT" : "INT"}
                sx={{ m: -1, mx: -2, p: 0 }}
                color={audit.type === EXTERNAL ? "primary" : null}
              />
            </Tooltip>
          ),
          branch: (
            <MDTypography
              variant="inherit"
              color="inherit"
              component={
                !bulkStatusMode && (permissions?.update || permissions?.get)
                  ? Link
                  : null
              }
              to={!bulkStatusMode ? "/audits/" + audit.id : undefined}
              sx={
                !bulkStatusMode && (permissions?.update || permissions?.get)
                  ? {
                      cursor: "pointer",
                    }
                  : null
              }
            >
              {audit.branch.branchGroup.name + " - " + audit.branch.name}
            </MDTypography>
          ),
          branchCsv: audit.branch.name,
          date: (
            <MDTypography variant="inherit" color="inherit">
              {moment(new Date(audit.date)).format("MMM Do YYYY, h:mm a")}
            </MDTypography>
          ),
          dateCsv: moment(new Date(audit.date)).format("MMM Do YYYY, h:mm a"),
          description: (
            <MDTypography
              component={
                !bulkStatusMode && (permissions?.update || permissions?.get)
                  ? Link
                  : null
              }
              variant="inherit"
              color="inherit"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              to={!bulkStatusMode ? "/audits/" + audit.id : undefined}
            >
              {audit.description}
            </MDTypography>
          ),
          descriptionCsv: audit.description,
          assignedTo: (
            <MDBox display="flex" alignItems="center" gap={1}>
              <Avatar
                sx={{ width: 24, height: 24 }}
                src={audit?.assignedTo?.profileImage}
              />
              <MDTypography variant="inherit" color="inherit">
                {audit?.assignedTo?.name}
              </MDTypography>
            </MDBox>
          ),
          assignedToCsv: audit?.assignedTo?.name,
          status: (
            <MDBadge
              badgeContent={audit.status.description}
              variant="gradient"
              color={STATUS_COLOR[audit.status.id]?.preset ?? null}
              container
              width="90px"
            ></MDBadge>
          ),
          lastUpdated: (
            <Typography sx={{ fontSize: "14px" }}>
              {[
                audit.updatedBy?.name,
                moment(new Date(audit.updatedAt)).format("D/M/YYYY, h:mm a"),
              ]
                .filter(Boolean)
                .join(" - ")}
            </Typography>
          ),
          statusCsv: audit.status.description,
          actions: (
            <>
              {can("delete", "audit", audit) ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    changeOpen("alert", { audit });
                  }}
                >
                  <Icon>delete</Icon>
                </IconButton>
              ) : null}
            </>
          ),
        };
      }),
    );
  }, [audits, bulkStatusMode, selectedAuditIds]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const loadingId = Math.random();
    let filters = localStorage.getItem("all-audits-page_filters");
    let sortBy;
    if (filters) {
      try {
        filters = JSON.parse(filters);
        if (filters.sortBy && !searchParams.get("sortBy")) {
          setSearchParams((curr) => {
            curr.set("sortBy", filters.sortBy);
            sortBy = filters.sortBy;
            return curr;
          });
        }
      } catch {}
    }
    //startLoading(loadingId);
    fetchAudits(source.token, sortBy, loadingId);
    return () => {
      source.cancel("Component unmounted");
      //stopLoading(loadingId);
    };
  }, []);

  return (
    <>
      {(can("create", "audit", {
        type: INTERNAL,
      }) ||
        can("create", "audit", {
          type: EXTERNAL,
        })) &&
        Boolean(open.create) && (
          <AuditDialogv2
            open={Boolean(open.create)}
            setOpen={(value) => {
              changeOpen("create", value);
            }}
            type={open.create.type}
            onSubmit={handleCreateAudit}
          />
        )}
      <AlertDialog
        open={Boolean(open.alert)}
        setOpen={(value) => {
          changeOpen("alert", value);
        }}
        title={label("delete-alert-title")}
        content={label("delete-alert-message")}
        onOk={async () => {
          return await handleDeleteAudit(open.alert.audit);
        }}
      />
      <BulkAuditStatusDialog
        open={Boolean(open.bulkStatus)}
        setOpen={(value) => {
          changeOpen("bulkStatus", value);
        }}
        statuses={options.statuses}
        selectedCount={selectedAuditIds.length}
        onSubmit={handleBulkStatusUpdate}
      />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("audit-plural")}{" "}
                  {permissions.get ? `(${rows.length})` : ""}
                </MDTypography>
                <MDBox display="flex" flexDirection="row" gap={1}>
                  {permissions?.update && permissions?.get ? (
                    <>
                      {bulkStatusMode ? (
                        <MDButton
                          onClick={() => {
                            setBulkStatusMode(false);
                            setSelectedAuditIds([]);
                          }}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>close</Icon>
                          &nbsp;Cancel
                        </MDButton>
                      ) : null}
                      <Tooltip
                        title={
                          bulkStatusMode && selectedAuditIds.length === 0
                            ? "Select at least one audit"
                            : ""
                        }
                      >
                        <div>
                          <MDButton
                            onClick={() => {
                              if (!bulkStatusMode) {
                                setBulkStatusMode(true);
                                return;
                              }

                              changeOpen("bulkStatus", true);
                            }}
                            disabled={
                              bulkStatusMode && selectedAuditIds.length === 0
                            }
                          >
                            <Icon sx={{ fontWeight: "bold" }}>
                              {bulkStatusMode ? "edit" : "checklist"}
                            </Icon>
                            &nbsp;
                            {bulkStatusMode
                              ? `Change status (${selectedAuditIds.length})`
                              : "Bulk status"}
                          </MDButton>
                        </div>
                      </Tooltip>
                    </>
                  ) : null}

                  {can("create", "audit", {
                    type: INTERNAL,
                  }) &&
                  can("create", "audit", {
                    type: EXTERNAL,
                  }) ? (
                    <ExpandableButton
                      items={[
                        {
                          label: label("create-internal-audit-button"),
                          props: {
                            onClick: () => {
                              changeOpen("create", { type: INTERNAL });
                            },
                            disabled: !can("create", "audit", {
                              type: INTERNAL,
                            }),
                          },
                          tooltip: can(
                            "create",
                            "audit",
                            {
                              type: INTERNAL,
                            },
                            (reason) => reason.type,
                            () => null,
                          ),
                        },
                        {
                          label: label("create-external-audit-button"),
                          props: {
                            onClick: () => {
                              changeOpen("create", { type: EXTERNAL });
                            },
                            disabled: !can("create", "audit", {
                              type: EXTERNAL,
                            }),
                          },
                          tooltip: can(
                            "create",
                            "audit",
                            {
                              type: EXTERNAL,
                            },
                            (reason) => reason.type,
                            () => null,
                          ),
                        },
                      ]}
                      disabled={
                        !can("create", "audit", {
                          type: INTERNAL,
                        }) &&
                        !can("create", "audit", {
                          type: EXTERNAL,
                        })
                      }
                    >
                      <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                      &nbsp;{label("add-new-audit-button")}
                    </ExpandableButton>
                  ) : (
                    <Tooltip
                      title={
                        !can("create", "audit", {
                          type: EXTERNAL,
                        }) &&
                        !can("create", "audit", {
                          type: INTERNAL,
                        })
                          ? "You need a subscription to create audits"
                          : ""
                      }
                    >
                      <div>
                        <MDButton
                          onClick={() => {
                            const canCreateExternal = can("create", "audit", {
                              type: EXTERNAL,
                            });
                            const canCreateInternal = can("create", "audit", {
                              type: INTERNAL,
                            });

                            if (canCreateExternal) {
                              changeOpen("create", { type: EXTERNAL });
                            } else if (canCreateInternal) {
                              changeOpen("create", { type: INTERNAL });
                            } else {
                              console.log("Can create none");
                            }
                          }}
                          disabled={
                            !can("create", "audit", {
                              type: EXTERNAL,
                            }) &&
                            !can("create", "audit", {
                              type: INTERNAL,
                            })
                          }
                        >
                          <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                          &nbsp;{label("add-new-audit-button")}
                        </MDButton>
                      </div>
                    </Tooltip>
                  )}

                  {permissions.get && (
                    <CSVLink
                      className="downloadbtn"
                      filename={`audits_${Date()}.csv`}
                      data={csvData || [[]]}
                    >
                      <MDButton>
                        <Icon sx={{ fontWeight: "bold" }}>download</Icon>
                        &nbsp;{label("export-button")}
                      </MDButton>
                    </CSVLink>
                  )}
                </MDBox>
              </MDBox>
              {permissions?.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-audits"
                      externalLoading={
                        !(
                          (!permissions.get || options.statuses) &&
                          options.users &&
                          options.regions
                        )
                      }
                      internalLoading={!audits}
                      fullWidth
                      setData={setAudits}
                      setCount={setCount}
                      onSearch={async (request, callback) => {
                        if (userId) {
                          request.userId = userId;
                        }

                        setLastRequestQuery(request);

                        callback(
                          await auditsService.getAudits({
                            ...request,
                            skip:
                              (Number(searchParams.get("page") || 1) - 1) * 10,
                            take: 10,
                            sortBy: searchParams
                              .get("sortBy")
                              ?.replace("asce", "asc"),
                          }),
                        );
                      }}
                      filterByDate={true}
                      label={
                        label("search-label") + " " + label("audit-plural")
                      }
                      filterBys={filterBys}
                      filterBySelectsItems={filterBySelectsItems}
                    />
                  </MDBox>

                  <MDBox
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "start",
                    }}
                  >
                    <DataTable
                      table={{ columns: columns, rows: rows }}
                      isSorted={true}
                      canSort={[
                        label("date-singular"),
                        label("branch-singular"),
                        label("description-singular"),
                        label("assigned-to-singular"),
                        label("status-singular"),
                      ]}
                      loading={!audits}
                      isScrollable={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      manualSortFunction={(columnId, direction) => {
                        fetchAuditsOnly(null, null, `${columnId}-${direction}`);
                      }}
                      noEndBorder
                      setCsvData={bulkStatusMode ? null : setCsvData}
                    />
                    <MDBox sx={{ padding: 2 }}>
                      <Pagination
                        count={Math.floor(count / 10) + 1}
                        color={"info"}
                        variant="outlined"
                        page={Number(searchParams.get("page"))}
                        onChange={(_, newPage) => {
                          setSearchParams((curr) => {
                            curr.set("page", newPage);
                            return curr;
                          });

                          fetchAuditsOnly(null, newPage, null);
                        }}
                      />
                    </MDBox>
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}

function BulkAuditStatusDialog({
  open,
  setOpen,
  statuses,
  selectedCount,
  onSubmit,
}) {
  const { label } = useLanguage();
  const [statusId, setStatusId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const statusItems = useMemo(
    () =>
      statuses?.map((status) => ({
        id: status.id,
        label: status.description,
      })) || [],
    [statuses],
  );

  useEffect(() => {
    if (open) {
      setStatusId(null);
      setError(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          setError(null);
          const result = await onSubmit({ statusId });
          setLoading(false);

          if (result.success) {
            setOpen(false);
          } else {
            setError(result.error);
          }
        },
      }}
    >
      <DialogTitle>
        <MDTypography variant="h5">Change audits status</MDTypography>
        <MDTypography variant="button" color="text">
          {selectedCount} selected
        </MDTypography>
      </DialogTitle>
      <DialogContent>
        <DropdownSelect
          label={label("status-singular")}
          id="bulk-audit-status-selector"
          initialValue={statusId || ""}
          onChange={(newValue) => {
            setStatusId(newValue?.id || null);
          }}
          items={statusItems}
          required
          fullWidth
          loading={!statuses}
          inputProps={
            error
              ? {
                  error: true,
                  helperText: error,
                }
              : {}
          }
        />
      </DialogContent>
      <DialogActions>
        <MDButton
          onClick={() => {
            setOpen(false);
          }}
        >
          {label("cancel-button")}
        </MDButton>
        <LoadingButton
          loading={loading}
          type="submit"
          color="info"
          variant="gradient"
          disabled={!statusId}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
